import { Check } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { applyLocale, type Locale } from "@/i18n/index.ts";
import { applyTheme, readTheme, type Theme } from "@/lib/theme.ts";
import { cn } from "@/lib/cn.ts";

/** Theme preview tiles + a language segmented control, like the original's Appearance section. */
export function AppearanceSettings() {
  const { t, i18n } = useTranslation();
  const [theme, setTheme] = useState<Theme>(readTheme);
  const [saved, setSaved] = useState(false);
  const flash = () => { setSaved(true); setTimeout(() => setSaved(false), 1500); };
  const themes: { id: Theme; label: string; preview: string }[] = [
    { id: "dark", label: t("common.themeDark"), preview: "bg-[#15171c]" },
    { id: "light", label: t("common.themeLight"), preview: "bg-[#f4f5f7]" },
    { id: "system", label: t("common.themeSystem"), preview: "bg-[linear-gradient(135deg,#15171c_50%,#f4f5f7_50%)]" },
  ];
  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-fg-muted">{t("common.theme")}</div>
        <div role="radiogroup" className="flex flex-wrap gap-3">
          {themes.map((th) => (
            <button key={th.id} type="button" role="radio" aria-checked={theme === th.id} onClick={() => { setTheme(th.id); applyTheme(th.id); flash(); }} className={cn("w-32 rounded-xl border p-2 text-center text-xs transition", theme === th.id ? "border-accent bg-accent-soft/40" : "border-border bg-surface hover:border-fg-muted/50")}>
              <div className={cn("mb-2 h-12 rounded-md border border-border/60", th.preview)} />
              {th.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-fg-muted">{t("common.language")}</div>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-surface px-4 py-3">
          <div>
            <div className="text-sm font-medium">{t("settings.appearance.rowTitle")}</div>
            <div className="text-xs text-fg-muted">{t("settings.appearance.rowHint")}</div>
          </div>
          <div role="radiogroup" className="flex rounded-md border border-border p-0.5">
            {(["ar", "en"] as Locale[]).map((l) => (
              <button key={l} type="button" role="radio" aria-checked={i18n.language === l} onClick={() => { applyLocale(l); flash(); }} className={cn("rounded px-3 py-1 text-sm", i18n.language === l ? "bg-accent text-accent-fg" : "text-fg-muted hover:text-fg")}>{l === "ar" ? "العربية" : "English"}</button>
            ))}
          </div>
        </div>
      </div>
      {saved && <div className="flex items-center gap-1 text-xs text-accent"><Check className="size-3.5" /> {t("common.saved")}</div>}
    </div>
  );
}
